import React, {useEffect, useState} from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, TextField, Button, Alert, CircularProgress, Link as MUILink, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SessionCreate from '../../graphql/mutations/SessionCreate';
import Note from './Note';
import { useTranslation } from 'react-i18next';

const SignIn = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        window.localStorage.clear();
    }, []);

    const handleSignIn = () => {
        try {
            setIsLoading(true);
            SessionCreate.commit({ email, password }).then(({ sessionCreate }) => {
                const { token, error } = sessionCreate || {};
                window.localStorage.clear();

                if (token) {
                  const { user: { paymentAccountStripeUid, dbId } } = sessionCreate;
                    localStorage.setItem('authToken', token);
                    localStorage.setItem('uid', dbId);
                    localStorage.setItem('paymentAccountStripeUid', paymentAccountStripeUid);
                    navigate('/catalogue'); // Redirect to home page
                    window.location.reload(true);
                } else {
                    setError(error || t('somethingWentWrong', 'Something went wrong'));
                }
                setIsLoading(false);
            })
        } catch (err) {
            setIsLoading(false);
            console.error('Sign-in error:', err);
            toast.error(t('unexpectedError', 'An unexpected error occurred.'));
        }
    };

    return (
        <Container maxWidth="lg">
            <Box
                sx={{
                    maxWidth: '400px',
                    margin: '50px auto',
                    padding: 4,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: 'primary.main',
                    }}
                >
                    {t('signIn', 'Sign In')}
                </Typography>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box component="form" noValidate autoComplete="off">
                    <TextField
                        fullWidth
                        type="email"
                        slotProps={{ inputLabel: { shrink: true } }}
                        autoComplete="off"
                        label={t('email', 'Email')}
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                        autoComplete="off"
                        label={t('password', 'Password')}
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 3 }}
                    />
                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        disabled={isLoading || password.length === 0 || email.length === 0}
                        onClick={handleSignIn}
                        sx={{
                            fontSize: '1rem',
                            padding: '12px 0',
                            textTransform: 'none'
                        }}
                    >
                        {isLoading && <CircularProgress size={20} color="inherit" sx={{ marginRight: '15px' }} />}
                        {t('signIn', 'Sign In')}
                    </Button>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 2,
                        }}
                    >
                        <Typography variant="body2">
                            <Link
                                to="/sign-up"
                                style={{ color: '#80a748', textDecoration: 'none' }}
                            >
                                {t('signUp', 'Sign up')}
                            </Link>
                        </Typography>

                        <Typography variant="body2">
                            <Link
                                to="/forgot-password"
                                style={{ color: '#80a748', textDecoration: 'none' }}
                            >
                                {t('forgotPassword', 'Forgot password?')}
                            </Link>
                        </Typography>
                    </Box>
                    <Note i18nKey="signInNoteRich" />
                </Box>
            </Box>
        </Container>
    );
};

export default SignIn;
